import { IonButton, IonIcon } from "@ionic/react";
import { logEvent } from "@src/appV2/lib/analytics";
import { USER_EVENTS } from "@src/constants/userEvents";
import { cashOutline, stopwatchOutline } from "ionicons/icons";
import { FC, useEffect, useMemo, useState } from "react";

import { formatTimeNegotiationStartAndEndTime } from "./helper";
import {
  INegotiationButton,
  INegotiationCommitModalProps,
  NegotiationHistoryAction,
  NegotiationRateRole,
  NegotiationStatus,
  NegotiationType,
} from "./interfaces";
import { NegotiationCommitModal } from "./NegotiationCommitModal";
import { NegotiationTool } from "./negotiationTool";
import "./style.scss";

export const NegotiationButton: FC<INegotiationButton> = ({
  shiftDetails,
  loadAlertDialog,
  workApproved,
  updateShiftViewRateNegotiation,
  isLoadingShiftBook,
  shouldOpenNegotiationTool,
  isRateNegotiationActive,
}) => {
  const [shift, setShift] = useState(shiftDetails);

  useEffect(() => {
    setShift(shiftDetails);
  }, [shiftDetails]);

  const [requestLoading, setRequestLoading] = useState(false);

  const updateShiftRateNegotiation = (negotiationData) =>
    setShift({ ...shift, rateNegotiation: negotiationData });

  const [negotiationCommitModalProps, setNegotiationCommitModalProps] =
    useState<INegotiationCommitModalProps>();

  const [openNegotiationTool, setOpenNegotiationTool] = useState<boolean>(
    shouldOpenNegotiationTool ?? false
  );
  const [negotiationType, setNegotiationType] = useState<NegotiationType | undefined>(
    shift?.rateNegotiation?.type
  );

  const closeNegotiationTool = () => {
    setOpenNegotiationTool(false);
    if (updateShiftViewRateNegotiation && shift.rateNegotiation) {
      updateShiftViewRateNegotiation(shift);
    }
  };

  const btnDetail = useMemo(() => {
    let details: {
      btnCustomProps: Record<string, unknown>;
      btnText: string;
    } = {
      btnCustomProps: {
        className: `negotiation-new-rate ${
          !isRateNegotiationActive ? "negotiation-new-rate-hidden" : ""
        }`,
        fill: "clear",
        color: "success",
      },
      btnText: "Propose new Rate",
    };
    const rateNegotiation = shift?.rateNegotiation;
    if (!rateNegotiation) {
      return details;
    }
    const disabledButtonProps = {
      color: "#828282",
      fill: "outline",
      style: {
        color: "#828282",
      },
    };
    const {
      payRate,
      start,
      end,
      createdByUserRole,
      action: lastAction,
    } = rateNegotiation.metadata.lastOffer;
    switch (rateNegotiation.status) {
      case NegotiationStatus.OPEN:
        if (createdByUserRole === NegotiationRateRole.WORKER) {
          const timeRangeContent =
            start && end && shift.facility?.tmz
              ? `: ${formatTimeNegotiationStartAndEndTime({
                  start,
                  end,
                  timezone: shift.facility?.tmz,
                })}`
              : "";
          const rateText = payRate ? `: $${payRate}/hr` : "";
          const buttonContent =
            "You proposed" +
            (rateNegotiation.type === NegotiationType.TIME ? timeRangeContent : rateText);
          details = {
            btnCustomProps: {
              fill: "outline",
              color: "success",
            },
            btnText: buttonContent,
          };
        } else {
          details = {
            btnCustomProps: {
              color: "success",
              fill: "solid",
            },
            btnText: `Reply to proposal: $${payRate}/hr`,
          };
        }
        break;
      case NegotiationStatus.CLOSED:
        if (lastAction === NegotiationHistoryAction.BOOKED_CONFLICTING_SHIFT) {
          details = {
            btnCustomProps: disabledButtonProps,
            btnText: `Ended by you`,
          };
        } else if (lastAction === NegotiationHistoryAction.BOOKED_BY_HCP) {
          details = {
            btnCustomProps: disabledButtonProps,
            btnText: `Booked by you`,
          };
        } else {
          details = {
            btnCustomProps: disabledButtonProps,
            btnText: `Booked by another Worker`,
          };
        }
        break;
      case NegotiationStatus.EXPIRED:
        details = {
          btnCustomProps: disabledButtonProps,
          btnText: `Expired before shift start`,
        };
        break;
      case NegotiationStatus.REJECTED:
        if (createdByUserRole === NegotiationRateRole.WORKER) {
          details = {
            btnCustomProps: disabledButtonProps,
            btnText: `Ended by you`,
          };
        } else {
          details = {
            btnCustomProps: disabledButtonProps,
            btnText: `Ended by Workplace`,
          };
        }
        break;
      case NegotiationStatus.ACCEPTED:
        details = {
          btnCustomProps: disabledButtonProps,
          btnText: `You're Working This Shift!`,
        };
        break;
    }
    return details;
  }, [isRateNegotiationActive, shift.facility?.tmz, shift?.rateNegotiation]);

  const closeDisplayCommitModal = () =>
    setNegotiationCommitModalProps({
      ...negotiationCommitModalProps,
      isOpen: false,
    } as INegotiationCommitModalProps);

  const handleDisplayCommitModal = (props) => {
    setNegotiationCommitModalProps(props);
  };

  const toolOpenedTrackEvent = () => {
    const history = shift?.rateNegotiation?.history || [];
    const trackProperties = {
      negotiationId: shift?.rateNegotiation?._id ?? "",
      shiftId: shift._id,
      new: history?.length === 0,
      type: negotiationType,
      proposals: history.filter(
        (historyItem) =>
          historyItem.action === NegotiationHistoryAction.PROPOSED ||
          historyItem.action === NegotiationHistoryAction.STARTED
      ).length,
    };

    logEvent(USER_EVENTS.NEGOTIABLE_MODAL_VIEWED, trackProperties);
  };

  const handleOpenNegotiation = (negotiationTypeProp: NegotiationType) => {
    setNegotiationType(negotiationTypeProp);
    if (!shift?.rateNegotiation) {
      loadAlertDialog(() => {
        setOpenNegotiationTool(true);
        toolOpenedTrackEvent();
      });
      return;
    }

    setOpenNegotiationTool(true);
    toolOpenedTrackEvent();
  };

  return (
    <div className="negotiation-btn-container">
      {!workApproved && (
        <>
          {shift.shiftTimeNegotiationActive && !shift.rateNegotiation && (
            <IonButton
              className="negotiation-new-rate"
              shape="round"
              onClick={() => handleOpenNegotiation(NegotiationType.TIME)}
              fill="clear"
              color="success"
            >
              <IonIcon slot="start" icon={stopwatchOutline}></IonIcon>
              Propose an alternative time
            </IonButton>
          )}
          <IonButton
            className="shift-ion-button ion-button"
            shape="round"
            onClick={() =>
              handleOpenNegotiation(shift.rateNegotiation?.type || NegotiationType.RATE)
            }
            data-testid="negotiation-button"
            {...btnDetail?.btnCustomProps}
            {...(!isRateNegotiationActive ? { disabled: true } : {})}
          >
            {!shift.rateNegotiation && isRateNegotiationActive && (
              <IonIcon slot="start" icon={cashOutline}></IonIcon>
            )}

            {btnDetail?.btnText}
          </IonButton>
        </>
      )}
      {openNegotiationTool && (
        <NegotiationTool
          isOpen={openNegotiationTool}
          closeModal={closeNegotiationTool}
          shift={shift}
          loadAlertDialog={loadAlertDialog}
          showCommitModal={handleDisplayCommitModal}
          closeCommitModal={closeDisplayCommitModal}
          workApproved={workApproved}
          updateShiftRateNegotiation={updateShiftRateNegotiation}
          setRequestLoading={setRequestLoading}
          isLoadingShiftBook={isLoadingShiftBook}
          negotiationType={negotiationType}
        />
      )}

      {negotiationCommitModalProps?.isOpen && (
        <NegotiationCommitModal
          {...negotiationCommitModalProps}
          closeModal={closeDisplayCommitModal}
          requestLoading={requestLoading}
        />
      )}
    </div>
  );
};
